import React, { useState, useEffect, useMemo } from 'react';
import { RiSendPlaneFill, RiEditLine, RiDeleteBinLine } from 'react-icons/ri';
import { BiCamera } from "react-icons/bi";
import { RiAttachment2 } from "react-icons/ri";
import axios from 'axios';
import { scheduleMessage, getScheduled, subscribe, cancelScheduled, loadScheduledFromBackend } from '../../utils/messageScheduler';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMessagesPage } from '../../features/messages/messagesSlice';
import { compressImages } from '../../utils/imageUtils';
import MessageImages from '../../components/MessageImages';

const API_URL = process.env.REACT_APP_API_URL;

const EmailManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [targetUser, setTargetUser] = useState(null);
  const [senderUser, setSenderUser] = useState(null); // Track who should send the message
  const [selectedRecord, setSelectedRecord] = useState(null); // Selected message row for DB updates
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, read, unread, replied
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledItems, setScheduledItems] = useState([]);
  const [scheduledItemsFromBackend, setScheduledItemsFromBackend] = useState([]);
  const [previousScheduledItems, setPreviousScheduledItems] = useState([]);
  const [dismissedSentNotifications, setDismissedSentNotifications] = useState(() => {
    // Load dismissed notifications from localStorage on mount
    try {
      const saved = localStorage.getItem('dismissedSentNotifications');
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(parsed);
      }
    } catch (err) {
      console.error('Failed to load dismissed notifications:', err);
    }
    return new Set();
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyMessage, setHistoryMessage] = useState(null);
  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isCompressingImages, setIsCompressingImages] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [messagesPerPage, setMessagesPerPage] = useState(50);
  const [showAll, setShowAll] = useState(false);

  // Redux-backed fetching
  const dispatch = useDispatch();
  const effectiveStatus = useMemo(() => filterStatus, [filterStatus]);
  const queryKey = useMemo(() => JSON.stringify({ page: currentPage, limit: messagesPerPage, search: searchTerm, status: effectiveStatus }), [currentPage, messagesPerPage, searchTerm, effectiveStatus]);
  const pageEntry = useSelector(state => state.messages.pagesByKey[queryKey]);
  const pageLoading = useSelector(state => state.messages.loadingByKey[queryKey]);

  useEffect(() => {
    const params = { page: currentPage, limit: messagesPerPage, search: searchTerm, status: effectiveStatus };
    dispatch(fetchMessagesPage(params));
  }, [dispatch, currentPage, messagesPerPage, searchTerm, effectiveStatus]);

  useEffect(() => {
    // Load scheduled messages from backend on mount
    const loadScheduled = async () => {
      try {
        await loadScheduledFromBackend('admin:email-mgmt');
        
        // Process any due scheduled messages first
        const token = localStorage.getItem('token');
        if (token) {
          try {
            // Trigger processing of due messages
            const processResponse = await axios.post(
              `${API_URL}/api/admin/scheduled-messages/process-due`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Show notification if messages were sent
            if (processResponse.data.recentlySent > 0) {
              alert(`${processResponse.data.recentlySent}件の予約メッセージを送信しました`);
            }
          } catch (err) {
            console.error('Failed to process due scheduled messages:', err);
          }
          
          // Fetch updated scheduled messages
          const response = await axios.get(
            `${API_URL}/api/admin/scheduled-messages?source=admin:email-mgmt`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const allItems = response.data.data || [];
          setScheduledItemsFromBackend(allItems);
          if (setPreviousScheduledItems) {
            setPreviousScheduledItems(allItems);
          }
          
          // Load dismissed notifications from localStorage
          try {
            const saved = localStorage.getItem('dismissedSentNotifications');
            if (saved) {
              const parsed = JSON.parse(saved);
              if (setDismissedSentNotifications) {
                setDismissedSentNotifications(new Set(parsed));
              }
            }
          } catch (err) {
            console.error('Failed to load dismissed notifications:', err);
          }
        }
      } catch (err) {
        console.error('Failed to load scheduled messages:', err);
      }
    };
    loadScheduled();

    // subscribe to scheduler updates for this context
    const unsubscribe = subscribe((items) => {
      setScheduledItems(items.filter((it) => it.source === 'admin:email-mgmt'));
    });
    setScheduledItems(getScheduled().filter((it) => it.source === 'admin:email-mgmt'));
    
    // Refresh backend data periodically and process due messages
    const refreshInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Process due messages
          try {
            const processResponse = await axios.post(
              `${API_URL}/api/admin/scheduled-messages/process-due`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Show notification if messages were sent (only if user is on the page)
            if (processResponse.data.recentlySent > 0) {
              // Use a less intrusive notification
              console.log(`${processResponse.data.recentlySent}件の予約メッセージを送信しました`);
            }
          } catch (err) {
            console.error('Failed to process due scheduled messages:', err);
          }
          
          // Fetch updated scheduled messages
          const response = await axios.get(
            `${API_URL}/api/admin/scheduled-messages?source=admin:email-mgmt`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const allItems = response.data.data || [];
          
          // Update previous state
          if (setPreviousScheduledItems) {
            setPreviousScheduledItems(allItems);
          }
          
          // Store all items (including sent ones) for display
          setScheduledItemsFromBackend(allItems);
          
          // Also sync with messageScheduler
          await loadScheduledFromBackend('admin:email-mgmt');
          
          // Log for debugging
          const sentCount = allItems.filter(item => item.status === 'sent').length;
          const pendingCount = allItems.filter(item => item.status === 'pending').length;
          if (sentCount > 0 || pendingCount > 0) {
            console.log(`Scheduled messages: ${pendingCount} pending, ${sentCount} sent`);
          }
        }
      } catch (err) {
        console.error('Failed to refresh scheduled messages:', err);
      }
    }, 10000); // Refresh every 10 seconds
    
    return () => {
      unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  // Emoji picker data
  const emojis = [
    '😊', '😄', '😍', '🥰', '😘', '😉', '😋', '😎', '🤗', '🤩',
    '👍', '👎', '👌', '✌️', '🤞', '👏', '🙌', '🤝', '💪', '👊',
    '❤️', '💖', '💕', '💗', '💓', '💝', '💘', '💞', '💟', '💌',
    '🌹', '🌸', '🌺', '🌷', '🌼', '🌻', '💐', '🌱', '🌿', '🍀',
    '🎉', '🎊', '🎈', '🎂', '🎁', '🎀', '🎪', '🎭', '🎨', '🎬',
    '🔥', '⭐', '🌟', '✨', '💫', '🌈', '☀️', '🌙', '🌍', '🌎'
  ];

  const addEmoji = (emoji) => {
    setSendMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Sync current Redux page into local state
  useEffect(() => {
    if (pageEntry?.messages) {
      setMessages(pageEntry.messages);
      setLoading(false);
    } else if (pageLoading) {
      setLoading(true);
    }
  }, [pageEntry, pageLoading]);

  const handleEditMessage = (message) => {
    setSelectedMessage(message);
    setEditContent(message.body);
    setShowEditModal(true);
  };

  const handleDeleteMessage = (message) => {
    setSelectedMessage(message);
    setShowDeleteModal(true);
  };

  const handleSendToUser = (targetUser, senderUser, sourceMessage) => {
    setTargetUser(targetUser);
    setSenderUser(senderUser);
    setSendMessage('');
    setEnableSchedule(false);
    setScheduledAt('');
    setShowSendModal(true);
    setSelectedRecord(sourceMessage || null);
  };

  const updateMessage = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/messages/${selectedMessage._id}`, {
        body: editContent
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Update local state
      setMessages(messages.map(msg =>
        msg._id === selectedMessage._id
          ? { ...msg, body: editContent }
          : msg
      ));

      setShowEditModal(false);
      setSelectedMessage(null);
      setEditContent('');
      alert('メッセージを更新しました');
    } catch (error) {
      console.error('Error updating message:', error);
      alert('メッセージの更新に失敗しました');
    }
  };

  const deleteMessage = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/messages/${selectedMessage._id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Remove from local state
      setMessages(messages.filter(msg => msg._id !== selectedMessage._id));

      setShowDeleteModal(false);
      setSelectedMessage(null);
      alert('メッセージを削除しました');
      
      // Refresh messages
      const params = { page: currentPage, limit: messagesPerPage, search: searchTerm, status: effectiveStatus };
      dispatch(fetchMessagesPage(params));
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('メッセージの削除に失敗しました');
    }
  };

  const handleSelectMessage = (messageId) => {
    setSelectedMessageIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedMessageIds.size === currentMessages.length) {
      setSelectedMessageIds(new Set());
    } else {
      setSelectedMessageIds(new Set(currentMessages.map(msg => msg._id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMessageIds.size === 0) {
      alert('削除するメッセージを選択してください');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const messageIdsArray = Array.from(selectedMessageIds);
      const response = await axios.post(
        `${API_URL}/api/admin/messages/bulk-delete`,
        { messageIds: messageIdsArray },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      alert(`${response.data.deletedCount}件のメッセージを削除しました`);
      
      // Clear selection
      setSelectedMessageIds(new Set());
      setShowBulkDeleteModal(false);
      
      // Refresh messages
      const params = { page: currentPage, limit: messagesPerPage, search: searchTerm, status: effectiveStatus };
      dispatch(fetchMessagesPage(params));
    } catch (error) {
      console.error('Error bulk deleting messages:', error);
      alert('メッセージの一括削除に失敗しました');
    }
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/') && !file.type.startsWith('video/'));
    
    if (imageFiles.length === 0) {
      alert('画像ファイルを選択してください（動画ファイルは除く）');
      return;
    }

    if (selectedImages.length + imageFiles.length > 10) {
      alert('画像は最大10枚まで選択できます');
      return;
    }

    setIsCompressingImages(true);
    try {
      const compressedImages = await compressImages(imageFiles);
      setSelectedImages(prev => [...prev, ...compressedImages]);
      setImagePreviewUrls(prev => [...prev, ...compressedImages]);
      // NOTE: Do not inject placeholder text into textarea; images are shown via previews/thumbnails.
    } catch (error) {
      console.error('Error compressing images:', error);
      alert('画像の圧縮に失敗しました');
    } finally {
      setIsCompressingImages(false);
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const sendNewMessage = async () => {
    try {
      if (!sendMessage.trim() && selectedImages.length === 0) {
        alert('メッセージまたは画像を入力してください');
        return;
      }

      // If scheduled for the future, push to scheduler and exit
      const isFuture = enableSchedule && scheduledAt && new Date(scheduledAt) > new Date();
      if (isFuture) {
        await scheduleMessage({
          recipients: [targetUser._id],
          content: sendMessage,
          images: selectedImages,
          scheduledAt,
          source: 'admin:email-mgmt',
          type: 'direct',
          senderUserId: senderUser._id, // Include sender info for scheduled messages
        });
        alert('メッセージを予約しました');
        
        // Refresh scheduled messages list
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const response = await axios.get(
              `${API_URL}/api/admin/scheduled-messages?source=admin:email-mgmt`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setScheduledItemsFromBackend(response.data.data || []);
          }
        } catch (err) {
          console.error('Failed to refresh scheduled messages:', err);
        }
        
        setShowSendModal(false);
        setTargetUser(null);
        setSenderUser(null);
        setSendMessage('');
        setSelectedImages([]);
        setImagePreviewUrls([]);
        setEnableSchedule(false);
        setScheduledAt('');
        return;
      }

      const token = localStorage.getItem('token');

      // Send message from senderUser to targetUser
      await axios.post(`${API_URL}/api/admin/messages/send-as-user`, {
        fromUserId: senderUser._id,
        toUserId: targetUser._id,
        content: sendMessage,
        images: selectedImages
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // If the selected record's recipient is Admin, mark that record read/replied in DB
      try {
        if (selectedRecord?.to_userID?.role === 'admin') {
          await axios.put(`${API_URL}/api/messages/${selectedRecord._id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
          await axios.put(`${API_URL}/api/messages/${selectedRecord._id}/reply`, {}, { headers: { Authorization: `Bearer ${token}` } });
        }
      } catch (_) {}

      setShowSendModal(false);
      setTargetUser(null);
      setSenderUser(null);
      setSelectedRecord(null);
      setSendMessage('');
      setSelectedImages([]);
      setImagePreviewUrls([]);
      setEnableSchedule(false);
      setScheduledAt('');
      alert('メッセージを送信しました');
      const params = { page: currentPage, limit: messagesPerPage, search: searchTerm, status: effectiveStatus };
      dispatch(fetchMessagesPage(params));
    } catch (error) {
      console.error('Error sending message:', error);
      alert('メッセージの送信に失敗しました');
    }
  };

  const handleViewDetail = (msg) => {
    setSelectedMessage(msg);
    setShowDetailModal(true);
  };

  const filteredMessages = messages.filter(msg => {
    const matchesSearch =
      msg.body.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.from_userID?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.to_userID?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.from_userID?.userID?.includes(searchTerm) ||
      msg.to_userID?.userID?.includes(searchTerm);

    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'read' && msg.is_read) ||
      (filterStatus === 'unread' && !msg.is_read) ||
      (filterStatus === 'replied' && msg.has_reply);

    return matchesSearch && matchesFilter;
  });

  // Pagination calculations (server-driven)
  const serverTotalPages = pageEntry?.pagination?.totalPages || 1;
  const totalPages = showAll ? 1 : serverTotalPages;
  const startIndex = showAll ? 0 : (currentPage - 1) * messagesPerPage;
  const endIndex = showAll ? filteredMessages.length : startIndex + (messages?.length || 0);
  const currentMessages = showAll ? filteredMessages : messages;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedMessageIds(new Set()); // Clear selection when filters change
  }, [searchTerm, filterStatus, messagesPerPage]);

  const handleMessagesPerPageChange = (newPerPage) => {
    setMessagesPerPage(newPerPage);
    setCurrentPage(1);
    setShowAll(false);
  };

  const handleShowAllToggle = () => {
    setShowAll(!showAll);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Conversation history utilities (for Send Message modal)
  const getConversationHistory = () => {
    if (!senderUser || !targetUser) return [];
    return messages
      .filter((m) => (
        (String(m.from_userID?._id) === String(senderUser?._id) && String(m.to_userID?._id) === String(targetUser?._id)) ||
        (String(m.from_userID?._id) === String(targetUser?._id) && String(m.to_userID?._id) === String(senderUser?._id))
      ))
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  const openHistoryInNewTab = () => {
    const history = getConversationHistory();
    const doc = window.open('', '_blank');
    if (!doc) return;
    const title = `${senderUser?.name || senderUser?.userID || ''} ⇄ ${targetUser?.name || targetUser?.userID || ''} のやり取り`;
    const html = `<!doctype html>
<html lang="ja">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans JP', 'Hiragino Kaku Gothic ProN', Meiryo, sans-serif; margin: 20px; }
    h2 { margin: 0 0 16px; }
    .msg { border: 1px solid #eee; border-radius: 8px; padding: 10px 12px; margin: 8px 0; }
    .meta { color: #666; font-size: 12px; margin-bottom: 6px; }
    .from { font-weight: bold; color: #34495e; }
    .body { white-space: pre-wrap; line-height: 1.6; }
  </style>
  </head>
<body>
  <h2>${title}</h2>
  ${history.map(h => `
    <div class="msg">
      <div class="meta">
        <span class="from">${h.from_userID?.name || h.from_userID?.userID || '不明'}</span>
        → ${h.to_userID?.name || h.to_userID?.userID || '不明'}
        ／ ${new Date(h.created_at).toLocaleString('ja-JP')}
      </div>
      <div class="body">${(h.body || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g,'&gt;')}</div>
    </div>
  `).join('')}
</body>
</html>`;
    doc.document.open();
    doc.document.write(html);
    doc.document.close();
  };

  const openHistoryDetail = (msg) => {
    setHistoryMessage(msg);
    setShowHistoryModal(true);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>読み込み中...</div>
      </div>
    );
  }

  const handleCancelScheduled = async (id) => {
    if (!window.confirm('この予約送信をキャンセルしますか？')) {
      return;
    }
    try {
      await cancelScheduled(id);
      
      // Refresh scheduled messages list
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(
            `${API_URL}/api/admin/scheduled-messages?source=admin:email-mgmt`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const allItems = response.data.data || [];
          setScheduledItemsFromBackend(allItems);
          setPreviousScheduledItems(allItems);
        }
      } catch (err) {
        console.error('Failed to refresh scheduled messages:', err);
      }
      
      alert('予約送信をキャンセルしました');
    } catch (err) {
      console.error('Error canceling scheduled message:', err);
      alert('予約送信のキャンセルに失敗しました');
    }
  };

  const handleDismissSentNotification = (itemId) => {
    setDismissedSentNotifications(prev => {
      const newSet = new Set([...prev, itemId]);
      // Save to localStorage for persistence
      try {
        localStorage.setItem('dismissedSentNotifications', JSON.stringify([...newSet]));
      } catch (err) {
        console.error('Failed to save dismissed notifications:', err);
      }
      return newSet;
    });
  };

  // Get sent messages that haven't been dismissed
  // Show all sent messages that are not dismissed, regardless of when they were sent
  // This ensures that any message that was in the pending list and got sent will show a notification
  const sentNotifications = scheduledItemsFromBackend
    .filter(item => {
      // Include sent messages that haven't been dismissed
      return item.status === 'sent' && !dismissedSentNotifications.has(item._id);
    })
    .sort((a, b) => {
      // Sort by scheduledAt (most recent first)
      return new Date(b.scheduledAt) - new Date(a.scheduledAt);
    });

  return (
    <div>
      <h2 style={{ color: '#2c3e50', marginBottom: '30px' }}>メール管理</h2>

      {/* Scheduled items panel */}
      <div style={{
        marginBottom: '20px',
        background: '#f8f9fa',
        border: '1px solid #eee',
        borderRadius: '8px',
        padding: '10px'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>⏳ 予約中のメッセージ</div>
        
        {/* Sent notifications */}
        {sentNotifications.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
            {sentNotifications.map((item) => (
              <div key={item._id} style={{
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                padding: '8px 10px',
                background: '#d4edda'
              }}>
                <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: '#155724', fontWeight: 'bold' }}>✅ メッセージが予約時間に正確に送信されました</span>
                </div>
                <div style={{ fontSize: '12px', color: '#155724' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(item.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ marginLeft: '8px' }}>
                    送信者: {item.senderUserId?.name || item.senderUserId?.userID || '不明'} → 
                    受信者: {Array.isArray(item.recipients) 
                      ? item.recipients.map(r => r.name || r.userID || r).join(', ')
                      : item.recipients}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', maxWidth: '600px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.content}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => handleDismissSentNotification(item._id)}
                    style={{
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    確認
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pending scheduled messages */}
        {scheduledItemsFromBackend.filter(item => item.status === 'pending').length === 0 && sentNotifications.length === 0 ? (
          <div style={{ color: '#7f8c8d', fontSize: '12px' }}>現在、予約はありません</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {scheduledItemsFromBackend
              .filter(item => item.status === 'pending')
              .map((item) => (
              <div key={item._id} style={{
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '6px 8px',
                background: item.status === 'failed' ? '#fdecea' : '#fcf3cf'
              }}>
                <div style={{ fontSize: '12px' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(item.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ color: '#7f8c8d' }}>{item.type}</span>
                  <span style={{ marginLeft: '8px', color: '#34495e' }}>
                    送信者: {item.senderUserId?.name || item.senderUserId?.userID || '不明'} → 
                    受信者: {Array.isArray(item.recipients) 
                      ? item.recipients.map(r => r.name || r.userID || r).join(', ')
                      : item.recipients}
                  </span>
                  {item.status === 'failed' && (
                    <span style={{ color: '#e74c3c', marginLeft: '6px' }}>失敗: {item.lastError}</span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', maxWidth: '600px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.content}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => handleCancelScheduled(item._id)}
                    style={{
                      background: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    取り消し
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search and Filter Controls */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="メッセージ内容、送信者、受信者で検索..."
          style={{
            padding: '10px 15px',
            border: '1px solid #ddd',
            borderRadius: '5px',
            width: '300px',
            fontSize: '14px'
          }}
        />
        {selectedMessageIds.size > 0 && (
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            style={{
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              padding: '10px 20px',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            選択した{selectedMessageIds.size}件を削除
          </button>
        )}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '10px 15px',
            border: '1px solid #ddd',
            borderRadius: '5px',
            fontSize: '14px'
          }}
        >
          <option value="all">すべて</option>
          <option value="read">既読</option>
          <option value="unread">未読</option>
          <option value="replied">返信済み</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <div style={{ color: '#666', fontSize: '14px' }}>
            総メッセージ数: <strong>{filteredMessages.length}</strong>件
            {!showAll && totalPages > 1 && (
              <span style={{ marginLeft: '10px' }}>
                ({startIndex + 1}-{Math.min(endIndex, filteredMessages.length)}件を表示中)
              </span>
            )}
          </div>

          {/* Messages per page selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>表示件数:</span>
            <select
              value={showAll ? 'all' : messagesPerPage}
              onChange={(e) => {
                if (e.target.value === 'all') {
                  handleShowAllToggle();
                } else {
                  handleMessagesPerPageChange(parseInt(e.target.value));
                }
              }}
              style={{
                padding: '5px 10px',
                border: '1px solid #ddd',
                borderRadius: '3px',
                fontSize: '14px'
              }}
            >
              <option value={25}>25件</option>
              <option value={50}>50件</option>
              <option value={100}>100件</option>
              <option value={200}>200件</option>
              <option value="all">すべて</option>
            </select>
          </div>
        </div>
      </div>
      {/* Pagination */}
      {!showAll && totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '20px',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          {/* Previous Button */}
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
              color: currentPage === 1 ? '#6c757d' : '#495057',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            前へ
          </button>

          {/* Page Numbers */}
          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} style={{ padding: '8px 4px', color: '#6c757d' }}>...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    backgroundColor: currentPage === page ? '#007bff' : 'white',
                    color: currentPage === page ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: currentPage === page ? 'bold' : 'normal'
                  }}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
              color: currentPage === totalPages ? '#6c757d' : '#495057',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            次へ
          </button>

          {/* Page Info and Go to Page */}
          {/* <div style={{
            marginLeft: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6c757d'
            }}>
              {currentPage} / {totalPages} ページ
            </div>

            {totalPages > 5 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ fontSize: '12px', color: '#6c757d' }}>ページへ:</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= totalPages) {
                      goToPage(page);
                    }
                  }}
                  style={{
                    width: '50px',
                    padding: '4px 6px',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    fontSize: '12px',
                    textAlign: 'center'
                  }}
                />
              </div>
            )}
          </div> */}
        </div>
      )}
      {/* History Message Modal */}
      {showHistoryModal && historyMessage && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'flex-start', zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '15px', borderRadius: '10px',
            width: '90%', maxWidth: '600px', height: '60vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              borderBottom: '1px solid #eee',
              paddingBottom: '10px',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>メッセージ詳細</h3>
              <button
                onClick={() => setShowHistoryModal(false)}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ✕
              </button>
            </div>
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #eee',
              padding: '10px',
              marginBottom: '12px',
              flexShrink: 0
            }}>
              <div style={{ color: '#666', fontSize: '12px' }}>
                <strong>{historyMessage.from_userID?.name || historyMessage.from_userID?.userID || '不明'}</strong>
                {' → '}
                <strong>{historyMessage.to_userID?.name || historyMessage.to_userID?.userID || '不明'}</strong>
                {' ／ '}{new Date(historyMessage.created_at).toLocaleString('ja-JP')}
              </div>
            </div>
            <div style={{
              backgroundColor: '#fff', padding: '10px', borderRadius: '8px',
              border: '1px solid #eee', flex: 1, overflow: 'auto',
              whiteSpace: 'pre-wrap', lineHeight: 1.6,
              wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%'
            }}>
              {historyMessage.body}
            </div>
          </div>
        </div>
      )}
      {/* Messages Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          tableLayout: 'fixed'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#34495e', color: 'white' }}>
              <th style={{ padding: '15px', textAlign: 'left', width: '60px' }}>No.</th>
              <th style={{ padding: '15px', textAlign: 'left', width: '20%' }}>送信者</th>
              <th style={{ padding: '15px', textAlign: 'left', width: '20%' }}>受信者</th>
              <th style={{ padding: '15px', textAlign: 'left', width: '35%' }}>内容</th>
              <th style={{ padding: '15px', textAlign: 'left', width: '15%' }}>日時</th>
              <th style={{ padding: '15px', textAlign: 'left', width: '10%' }}>操作</th>
              <th style={{ padding: '15px', textAlign: 'center', width: '50px' }}>
                <input
                  type="checkbox"
                  checked={currentMessages.length > 0 && selectedMessageIds.size === currentMessages.length}
                  onChange={handleSelectAll}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {currentMessages.map((msg, index) => (
              <tr key={msg._id} style={{
                borderBottom: '1px solid #ecf0f1',
                backgroundColor: selectedMessageIds.has(msg._id) 
                  ? '#e8f4f8' 
                  : (msg.is_read ? 'white' : '#fffbe6')
              }}>
                
                <td style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold' }}>
                  {startIndex + index + 1}
                </td>

                {/* Sender */}
                <td style={{ padding: '15px', verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ position: 'relative' }}>
                      {msg.from_userID?.photoLink ? (
                        <img
                          src={msg.from_userID.photoLink.photodatas || msg.from_userID.photoLink}
                          alt="Avatar"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}>
                          なし
                        </div>
                      )}
                      <button
                        onClick={() => handleSendToUser(msg.from_userID, msg.to_userID, msg)}
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          fontSize: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="このユーザーにメッセージを送信"
                      >
                        <RiSendPlaneFill />
                      </button>
                    </div>
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                        {msg.from_userID?.name || '不明'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                        ID: {msg.from_userID?.userID}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Recipient */}
                <td style={{ padding: '15px', verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ position: 'relative' }}>
                      {msg.to_userID?.photoLink ? (
                        <img
                          src={msg.to_userID.photoLink.photodatas || msg.to_userID.photoLink}
                          alt="Avatar"
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}>
                          なし
                        </div>
                      )}
                      <button
                        onClick={() => handleSendToUser(msg.to_userID, msg.from_userID)}
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          fontSize: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="このユーザーにメッセージを送信"
                      >
                        <RiSendPlaneFill />
                      </button>
                    </div>
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                        {msg.to_userID?.name || '不明'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                        ID: {msg.to_userID?.userID}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Message Content */}
                <td 
                  style={{
                    padding: '15px',
                    verticalAlign: 'top',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleViewDetail(msg)}
                >
                  <div style={{
                    lineHeight: '1.4',
                    marginBottom: '5px',
                    maxHeight: '3em',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {msg.body}
                    {msg.body && msg.body.length > 100 && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, white)',
                        width: '50px',
                        height: '1.5em'
                      }}></div>
                    )}
                  </div>
                  {msg.images && msg.images.length > 0 && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <MessageImages images={msg.images} style={{ maxHeight: '50px' }} />
                    </div>
                  )}
                  {/* {msg.body && msg.body.length > 100 && (
                    <div style={{
                      color: '#3498db',
                      fontSize: '12px',
                      marginTop: '5px',
                      fontWeight: 'bold'
                    }}>
                      クリックして詳細を表示
                    </div>
                  )} */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '5px', 
                    marginTop: '5px',
                    fontSize: '11px',
                    color: '#666'
                  }}>
                    {!msg.is_read && <span style={{ color: '#e74c3c' }}>未読</span>}
                    {msg.has_reply && <span style={{ color: '#27ae60' }}>返信済み</span>}
                  </div>
                </td>

                {/* Date */}
                <td style={{
                  padding: '15px',
                  fontSize: '12px',
                  color: '#7f8c8d',
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap'
                }}>
                  {new Date(msg.created_at).toLocaleString('ja-JP')}
                </td>

                {/* Actions */}
                <td style={{ padding: '15px', verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                      onClick={() => handleEditMessage(msg)}
                      style={{
                        backgroundColor: '#f39c12',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 8px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px'
                      }}
                      title="メッセージを編集"
                    >
                      <RiEditLine />
                    </button>
                    <button
                      onClick={() => handleDeleteMessage(msg)}
                      style={{
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 8px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px'
                      }}
                      title="メッセージを削除"
                    >
                      <RiDeleteBinLine />
                    </button>
                  </div>
                </td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedMessageIds.has(msg._id)}
                    onChange={() => handleSelectMessage(msg._id)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!showAll && totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '20px',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          {/* Previous Button */}
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
              color: currentPage === 1 ? '#6c757d' : '#495057',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            前へ
          </button>

          {/* Page Numbers */}
          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} style={{ padding: '8px 4px', color: '#6c757d' }}>...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    backgroundColor: currentPage === page ? '#007bff' : 'white',
                    color: currentPage === page ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: currentPage === page ? 'bold' : 'normal'
                  }}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
              color: currentPage === totalPages ? '#6c757d' : '#495057',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            次へ
          </button>

          {/* Page Info and Go to Page */}
          {/* <div style={{
            marginLeft: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#6c757d'
            }}>
              {currentPage} / {totalPages} ページ
            </div>

            {totalPages > 5 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ fontSize: '12px', color: '#6c757d' }}>ページへ:</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= totalPages) {
                      goToPage(page);
                    }
                  }}
                  style={{
                    width: '50px',
                    padding: '4px 6px',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    fontSize: '12px',
                    textAlign: 'center'
                  }}
                />
              </div>
            )}
          </div> */}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMessage && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '600px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>メッセージ編集</h3>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              style={{
                width: '95%',
                height: '200px',
                padding: '15px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={updateMessage}
                disabled={!editContent.trim()}
                style={{
                  backgroundColor: !editContent.trim() ? '#ccc' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: !editContent.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                更新
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedMessage && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#e74c3c' }}>メッセージ削除</h3>
            <p>このメッセージを削除してもよろしいですか？</p>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '5px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {selectedMessage.body.substring(0, 100)}...
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={deleteMessage}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                削除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#e74c3c' }}>一括削除</h3>
            <p>選択した<strong>{selectedMessageIds.size}件</strong>のメッセージを削除してもよろしいですか？</p>
            <p style={{ color: '#e74c3c', fontSize: '14px', marginTop: '10px' }}>
              この操作は取り消せません。
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleBulkDelete}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                削除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Message Modal */}
      {showSendModal && targetUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '500px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '20px' }}>
              {senderUser?.name}さんから{targetUser.name}さんにメッセージを送信
            </h3>

            {/* Conversation History */}
            <div style={{
              marginBottom: '15px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #eee',
              borderRadius: '8px',
              padding: '10px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontWeight: 'bold', color: '#34495e' }}>過去のやり取り</div>
                {/* <button
                  onClick={openHistoryInNewTab}
                  style={{
                    background: 'none',
                    border: '1px solid #3498db',
                    color: '#3498db',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  別タブで開く
                </button> */}
              </div>
              <div style={{
                maxHeight: '180px',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {getConversationHistory().length === 0 ? (
                  <div style={{ color: '#7f8c8d', fontSize: '12px' }}>やり取りはまだありません</div>
                ) : (
                  getConversationHistory().slice(-5).map((h, idx) => {
                    const firstLine = (h.body || '').split(/\r?\n/)[0];
                    return (
                      <div 
                        key={h._id || idx}
                        style={{
                          background: 'white',
                          border: '1px solid #eee',
                          borderRadius: '6px',
                          padding: '8px',
                          cursor: 'pointer'
                        }}
                        onClick={() => openHistoryDetail(h)}
                        title="クリックで全文を表示"
                      >
                        <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>
                          <strong>{h.from_userID?.name || h.from_userID?.userID || '不明'}</strong>
                          {' → '}
                          <strong>{h.to_userID?.name || h.to_userID?.userID || '不明'}</strong>
                          {' ／ '}{new Date(h.created_at).toLocaleString('ja-JP')}
                        </div>
                        <div style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.5,maxWidth: '100%'}}>
                          {firstLine}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div style={{ marginBottom: '3px' }}>
              <textarea
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                placeholder="メッセージを入力してください..."
                style={{
                  width: '95%',
                  height: '150px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  resize: 'vertical',
                  marginBottom: '10px'
                }}
              />
            </div>

            {/* Image Upload + Emoji controls (match ProfileSearch layout) */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <label
                    style={{
                      border: '1px solid #3498db',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <RiAttachment2 />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label
                    style={{
                      border: '1px solid #27ae60',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <BiCamera />
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <div>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: '8px 8px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    😊 絵文字
                  </button>
                </div>
              </div>
              {isCompressingImages && (
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                  画像を圧縮中...
                </div>
              )}
              {imagePreviewUrls.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '10px',
                  marginTop: '10px'
                }}>
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '5px',
                          border: '1px solid #ddd'
                        }}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                zIndex: 1001,
                maxWidth: '400px',
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '10px' }}>
                  {emojis.map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => addEmoji(emoji)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '5px',
                        borderRadius: '5px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowEmojiPicker(false)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Scheduling controls */}
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={enableSchedule}
                  onChange={(e) => setEnableSchedule(e.target.checked)}
                  style={{ marginRight: '6px' }}
                />
                後で送信を予約する
              </label>
            </div>
            {enableSchedule && (
              <div style={{ marginBottom: '10px' }}>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowSendModal(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={sendNewMessage}
                disabled={!sendMessage.trim() && selectedImages.length === 0}
                style={{
                  backgroundColor: (sendMessage.trim() || selectedImages.length > 0) ? '#27ae60' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (sendMessage.trim() || selectedImages.length > 0) ? 'pointer' : 'not-allowed'
                }}
              >
                {enableSchedule && scheduledAt && new Date(scheduledAt) > new Date() ? '予約' : '送信'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detailed View Modal */}
      {showDetailModal && selectedMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '10px',
            width: '90%',
            maxWidth: '600px',
            height: '60vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #eee',
              paddingBottom: '15px',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>メッセージ詳細</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Message Info */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              flexShrink: 0
            }}>
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  marginBottom: '10px'
                }}>
                  {selectedMessage.from_userID?.photoLink ? (
                    <img
                      src={selectedMessage.from_userID.photoLink.photodatas || selectedMessage.from_userID.photoLink}
                      alt="Profile"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#ddd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px'
                    }}>
                      なし
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      送信者: {selectedMessage.from_userID?.name || '不明'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      ID: {selectedMessage.from_userID?.userID}
                    </div>
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  {selectedMessage.to_userID?.photoLink ? (
                    <img
                      src={selectedMessage.to_userID.photoLink.photodatas || selectedMessage.to_userID.photoLink}
                      alt="Profile"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#ddd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px'
                    }}>
                      なし
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      受信者: {selectedMessage.to_userID?.name || '不明'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      ID: {selectedMessage.to_userID?.userID}
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                  {new Date(selectedMessage.created_at).toLocaleString('ja-JP')}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: selectedMessage.is_read ? '#27ae60' : '#e74c3c',
                  fontWeight: 'bold'
                }}>
                  {selectedMessage.is_read ? '既読' : '未読'}
                </div>
                {selectedMessage.has_reply && (
                  <div style={{ fontSize: '12px', color: '#27ae60', fontWeight: 'bold' }}>
                    返信済み
                  </div>
                )}
              </div>
            </div>
            
            {/* Message Content */}
            <div style={{
              backgroundColor: '#fff',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #eee',
              marginBottom: '20px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#333', flexShrink: 0 }}>メッセージ内容</h4>
              <div style={{
                lineHeight: '1.6',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '16px',
                color: '#333',
                overflow: 'auto',
                flex: 1,
                marginBottom: selectedMessage.images && selectedMessage.images.length > 0 ? '15px' : '0'
              }}>
                {selectedMessage.body}
              </div>
              {selectedMessage.images && selectedMessage.images.length > 0 && (
                <div style={{ flexShrink: 0 }}>
                  <MessageImages images={selectedMessage.images} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailManagement;
